import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { useEffect, useState } from "react";

const CHART_COLORS_LIGHT = ["#2563EB", "#3B82F6", "#93C5FD", "#E11D48", "#FDA4AF", "#1D4ED8", "#60A5FA"];
const CHART_COLORS_DARK = ["#E5D6B8", "#D4C3A3", "#C9B896", "#BBA47D", "#AD9268", "#9C8154", "#8B7043"];

function useIsDark() {
  const [dark, setDark] = useState(() => typeof document !== "undefined" && document.documentElement.classList.contains("dark"));
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setDark(el.classList.contains("dark")));
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

function StatCard({ label, value, accent, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-border bg-card p-6 hover:-translate-y-1 hover:shadow-sm transition-transform duration-200">
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-2">{label}</p>
      <p className={`font-heading text-3xl font-extrabold tracking-tight ${accent || ""}`}>{value}</p>
    </div>
  );
}

export default function Stats({ data }) {
  const dark = useIsDark();
  if (!data) return null;
  const palette = dark ? CHART_COLORS_DARK : CHART_COLORS_LIGHT;
  const chartData = data.category_breakdown.slice(0, 8);
  const fmt = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <StatCard testId="stat-month" label="This Month" value={fmt(data.month_total)} accent="text-primary" />
        <StatCard testId="stat-ytd" label="Year to Date" value={fmt(data.ytd_total)} />
        <StatCard testId="stat-alltime" label="All Time" value={fmt(data.all_time_total)} />
        <StatCard testId="stat-count" label="Receipts" value={data.receipt_count} accent="text-destructive" />
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <p className="font-heading font-bold text-lg mb-1">Spend by category</p>
        <p className="text-sm text-muted-foreground mb-6">Deductible totals across all receipts</p>
        {chartData.length ? (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20 }}>
              <XAxis type="number" tickFormatter={(v) => `$${v}`} stroke={dark ? "#71717a" : "#94a3b8"} fontSize={12} />
              <YAxis type="category" dataKey="category" width={140} stroke={dark ? "#a1a1aa" : "#475569"} fontSize={11} />
              <Tooltip
                formatter={(v) => fmt(v)}
                cursor={{ fill: dark ? "rgba(212,195,163,0.08)" : "#f1f5f9" }}
                contentStyle={dark ? { background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fafafa" } : undefined}
                labelStyle={dark ? { color: "#fafafa" } : undefined}
              />
              <Bar dataKey="total" radius={[0, 6, 6, 0]}>
                {chartData.map((_, i) => <Cell key={i} fill={palette[i % palette.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-muted-foreground py-12 text-center">Add receipts to see your breakdown.</p>
        )}
      </div>
    </div>
  );
}
